import { useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { routeKeys } from './useTechnicianRoutes';
import {
  createTechnicianAssignment,
  deleteTechnicianAssignment,
  updateTechnicianAssignment,
  checkExistingAssignment,
  CreateAssignmentData,
  UpdateAssignmentData,
  TechnicianAssignment,
  getTechnicianAssignments
} from '@/lib/supabase/technicianAssignments';
import {
  syncAssignmentsToRoute,
  formatRouteDate,
  deleteRouteByTechnicianDate,
  getRouteByTechnicianDate
} from '@/lib/supabase/technicianRoutes';
import type { RouteStartLocation } from '@/types/technicianRoutes';
import { getProdSupabaseClient } from '@/lib/supabase/client';
import { logUserActivity } from '@/lib/activity/userActivity';

export interface RecallToAssign {
  vin: string;
  campaign_number: string;
  recall_title?: string;
  dealer_name?: string;
  dealer_id?: string;
  dealer_city?: string;
  dealer_region?: string;
  dealer_address?: string;
  dealer_phone?: string;
  year?: string | number;
  make?: string;
  model?: string;
  // Manual stop fields (matching DB schema)
  stop_type?: 'recall' | 'manual';
  manual_stop_reason?: string;
  manual_stop_lat?: number;
  manual_stop_lng?: number;
  notes?: string;
}

export interface AssignmentResult {
  success: boolean;
  assignmentId?: string;
  error?: string;
}

export interface BulkAssignmentResult {
  successful: number;
  failed: number;
  errors: string[];
}

/**
 * Hook for auto-saving technician assignments with optimistic updates and undo capability
 */
export const useAutoSaveAssignment = (onAssignmentChange?: () => void) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isAssigning, setIsAssigning] = useState(false);

  // Get assigner name from user metadata or email
  const assignerName =
    user?.user_metadata?.['full_name'] || user?.email?.split('@')[0] || 'Unknown';
  const [recentAssignments, setRecentAssignments] = useState<TechnicianAssignment[]>([]);
  const undoTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Assign a single recall to a technician with auto-save
   */
  const assignRecall = useCallback(
    async (
      technicianId: string,
      technicianName: string,
      recall: RecallToAssign,
      expectedVisitDate?: string
    ): Promise<AssignmentResult> => {
      setIsAssigning(true);

      try {
        // Check for existing assignment
        const existing = await checkExistingAssignment(recall.vin, recall.campaign_number);

        if (existing) {
          // Check if date is changing (normalize dates for comparison)
          const existingDate = existing.expected_visit_date
            ? new Date(existing.expected_visit_date).toISOString().split('T')[0]
            : null;
          const newDate = expectedVisitDate
            ? new Date(expectedVisitDate).toISOString().split('T')[0]
            : null;
          const dateChanged = existingDate !== newDate;

          // If assigned to same technician AND same date, skip
          if (existing.technician_id === technicianId && !dateChanged) {
            toast({
              title: 'Already Assigned',
              description: `This recall is already assigned to ${technicianName} with the same schedule date`,
              variant: 'default'
            });
            return { success: false, error: 'Already assigned to same technician with same date' };
          }

          // Update assignment (reassignment or schedule date change)
          const updateData: UpdateAssignmentData = {
            technician_id: technicianId,
            expected_visit_date: expectedVisitDate,
            assignment_status: 'assigned',
            priority: 'medium'
          };

          try {
            const result = await updateTechnicianAssignment(existing.id, updateData);

            if (result) {
              // Track for potential undo (we'll track the new assignment)
              const updatedAssignment: TechnicianAssignment = {
                ...result,
                technician_name: technicianName
              };
              setRecentAssignments(prev => [...prev, updatedAssignment]);

              // Show success toast with appropriate message
              if (existing.technician_id === technicianId && dateChanged) {
                toast({
                  title: 'Schedule Updated',
                  description: `Schedule date updated for ${technicianName}`
                });
              } else {
                toast({
                  title: 'Recall Re-assigned',
                  description: `Re-assigned from ${existing.technician_name || 'previous technician'} to ${technicianName}`
                });
              }

              logUserActivity({
                category: 'routing',
                action:
                  existing.technician_id === technicianId ? 'update_schedule' : 'reassign_recall',
                entityType: 'assignment',
                entityId: existing.id,
                entityName: `${recall.vin} - ${recall.campaign_number}`,
                metadata: {
                  vin: recall.vin,
                  campaign_number: recall.campaign_number,
                  technician_name: technicianName
                }
              });

              // Notify parent of change
              onAssignmentChange?.();

              // Sync to route table (background operation)
              if (expectedVisitDate) {
                syncAssignmentsToRouteAsync(technicianId, expectedVisitDate);
              }

              return { success: true, assignmentId: result.id };
            }

            return { success: false, error: 'Failed to update assignment' };
          } catch (updateError: any) {
            throw updateError;
          }
        }

        // Create new assignment if none exists
        const assignmentData: CreateAssignmentData = {
          technician_id: technicianId,
          vin: recall.vin,
          campaign_number: recall.campaign_number,
          recall_title: recall.recall_title,
          dealer_name: recall.dealer_name,
          dealer_id: recall.dealer_id,
          dealer_city: recall.dealer_city,
          dealer_region: recall.dealer_region,
          dealer_address: recall.dealer_address,
          dealer_phone: recall.dealer_phone,
          dealer_postal_code: recall.dealer_postal_code,
          assignment_status: 'assigned',
          priority: 'medium',
          expected_visit_date: expectedVisitDate,
          assigned_by: user?.id,
          assigned_by_name: assignerName,
          // Vehicle info for display in route tracker
          vehicle_year: recall.year ? parseInt(String(recall.year), 10) : undefined,
          vehicle_make: recall.make,
          vehicle_model: recall.model,
          // Manual stop fields (only fields that exist in DB schema)
          stop_type: recall.stop_type,
          manual_stop_reason: recall.manual_stop_reason,
          manual_stop_lat: recall.manual_stop_lat,
          manual_stop_lng: recall.manual_stop_lng
        };

        const result = await createTechnicianAssignment(assignmentData);

        if (result) {
          // Track for potential undo
          setRecentAssignments(prev => [...prev, result]);

          logUserActivity({
            category: 'routing',
            action: 'assign_recall',
            entityType: 'assignment',
            entityId: result.id,
            entityName: `${recall.vin} - ${recall.campaign_number}`,
            metadata: {
              vin: recall.vin,
              campaign_number: recall.campaign_number,
              technician_name: technicianName
            }
          });

          // Show success toast
          toast({
            title: 'Recall Assigned',
            description: `Assigned to ${technicianName}`
          });

          // Notify parent of change
          onAssignmentChange?.();

          // Sync to route table (background operation)
          if (expectedVisitDate) {
            syncAssignmentsToRouteAsync(technicianId, expectedVisitDate);
          }

          return { success: true, assignmentId: result.id };
        }

        return { success: false, error: 'Failed to create assignment' };
      } catch (error: any) {
        const errorMessage =
          error?.message || error?.error?.message || error?.toString() || 'Unknown error occurred';

        toast({
          title: 'Assignment Failed',
          description: errorMessage,
          variant: 'destructive'
        });
        return { success: false, error: errorMessage };
      } finally {
        setIsAssigning(false);
      }
    },
    [toast, onAssignmentChange]
  );

  /**
   * Bulk assign multiple recalls to a technician
   * @param startLocation - The starting location for the route (from UI selection)
   */
  const bulkAssignRecalls = useCallback(
    async (
      technicianId: string,
      technicianName: string,
      recalls: RecallToAssign[],
      expectedVisitDate?: string,
      startLocation?: RouteStartLocation
    ): Promise<BulkAssignmentResult> => {
      setIsAssigning(true);
      const results: BulkAssignmentResult = {
        successful: 0,
        failed: 0,
        errors: []
      };

      const newAssignments: TechnicianAssignment[] = [];

      // Track original technicians for route re-sync after reassignment
      // Key: "technicianId|date", Value: { technicianId, expectedVisitDate }
      const affectedOriginalRoutes = new Map<
        string,
        { technicianId: string; expectedVisitDate: string }
      >();

      try {
        // Process assignments sequentially to avoid race conditions
        for (const recall of recalls) {
          try {
            // Check for existing assignment
            const existing = await checkExistingAssignment(recall.vin, recall.campaign_number);

            if (existing) {
              // Check if date is changing (normalize dates for comparison)
              const existingDate = existing.expected_visit_date
                ? new Date(existing.expected_visit_date).toISOString().split('T')[0]
                : null;
              const newDate = expectedVisitDate
                ? new Date(expectedVisitDate).toISOString().split('T')[0]
                : null;
              const dateChanged = existingDate !== newDate;

              // If assigned to same technician AND same date, skip silently (this is a no-op)
              // This can happen when user changes start location and clicks Assign again
              // We don't want to count this as a failure - it's just already done
              if (existing.technician_id === technicianId && !dateChanged) {
                // Count as successful (no change needed) rather than failed
                results.successful++;
                continue;
              }

              // Track the original route for re-sync if this is a reassignment
              // Covers: different technician, OR same technician with a date change
              // The old route needs to be re-synced to remove the moved stops
              if (
                existing.expected_visit_date &&
                (existing.technician_id !== technicianId || dateChanged)
              ) {
                const origDate = new Date(existing.expected_visit_date).toISOString().split('T')[0];
                const key = `${existing.technician_id}|${origDate}`;
                affectedOriginalRoutes.set(key, {
                  technicianId: existing.technician_id,
                  expectedVisitDate: origDate
                });
              }

              // Update assignment (reassignment or schedule date change)
              const updateData: UpdateAssignmentData = {
                technician_id: technicianId,
                expected_visit_date: expectedVisitDate,
                assignment_status: 'assigned',
                priority: 'medium'
              };

              try {
                const result = await updateTechnicianAssignment(existing.id, updateData);
                if (result) {
                  results.successful++;
                  const updatedAssignment: TechnicianAssignment = {
                    ...result,
                    technician_name: technicianName
                  };
                  newAssignments.push(updatedAssignment);
                } else {
                  results.failed++;
                  results.errors.push(`Failed to re-assign ${recall.vin}`);
                }
              } catch (updateErr: any) {
                results.failed++;
                results.errors.push(
                  `${recall.vin}: ${updateErr?.message || updateErr?.error?.message || 'Update failed'}`
                );
              }
              continue;
            }

            // Create new assignment if none exists
            const assignmentData: CreateAssignmentData = {
              technician_id: technicianId,
              vin: recall.vin,
              campaign_number: recall.campaign_number,
              recall_title: recall.recall_title,
              dealer_name: recall.dealer_name,
              dealer_id: recall.dealer_id,
              dealer_city: recall.dealer_city,
              dealer_region: recall.dealer_region,
              dealer_address: recall.dealer_address,
              dealer_phone: recall.dealer_phone,
              assignment_status: 'assigned',
              priority: 'medium',
              expected_visit_date: expectedVisitDate,
              assigned_by: user?.id,
              assigned_by_name: assignerName,
              // Vehicle info for display in route tracker
              vehicle_year: recall.year ? parseInt(String(recall.year), 10) : undefined,
              vehicle_make: recall.make,
              vehicle_model: recall.model,
              // Manual stop fields (only fields that exist in DB schema)
              stop_type: recall.stop_type,
              manual_stop_reason: recall.manual_stop_reason,
              manual_stop_lat: recall.manual_stop_lat,
              manual_stop_lng: recall.manual_stop_lng,
              notes: recall.notes
            };

            const result = await createTechnicianAssignment(assignmentData);
            if (result) {
              results.successful++;
              newAssignments.push(result);
            } else {
              results.failed++;
              results.errors.push(`Failed to assign ${recall.vin}`);
            }
          } catch (error) {
            results.failed++;
            results.errors.push(
              `${recall.vin}: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
          }
        }

        // Track for potential undo
        if (newAssignments.length > 0) {
          setRecentAssignments(prev => [...prev, ...newAssignments]);
        }

        // Log bulk assign activity
        if (results.successful > 0) {
          logUserActivity({
            category: 'routing',
            action: 'bulk_assign_recalls',
            entityType: 'assignment',
            entityName: technicianName,
            itemCount: results.successful,
            metadata: { technician_id: technicianId, failed: results.failed }
          });
        }

        // Show result toast
        if (results.successful > 0) {
          toast({
            title: 'Recalls Assigned',
            description: `${results.successful} recall${results.successful !== 1 ? 's' : ''} assigned to ${technicianName}${results.failed > 0 ? ` (${results.failed} failed)` : ''}`
          });
        } else if (results.failed > 0) {
          toast({
            title: 'Assignment Failed',
            description: `Failed to assign ${results.failed} recall${results.failed !== 1 ? 's' : ''}. ${results.errors[0] || 'Unknown error'}`,
            variant: 'destructive'
          });
        }

        // Sync assignments to route table and then notify parent.
        // IMPORTANT: Always sync the route to update the start location, even if
        // no new assignments. Pass the just-touched IDs through to eliminate the
        // read-after-write race that historically created orphan assignments.
        if (expectedVisitDate) {
          await syncAssignmentsToRouteAsync(
            technicianId,
            expectedVisitDate,
            startLocation,
            newAssignments.map(a => a.id)
          );
        }

        // Re-sync routes for original technicians that had recalls reassigned away from them
        // This ensures their routes are re-optimized after losing stops
        if (affectedOriginalRoutes.size > 0) {
          // eslint-disable-next-line no-console
          console.log(
            '[bulkAssignRecalls] Re-syncing routes for',
            affectedOriginalRoutes.size,
            'affected original technicians'
          );
          for (const affectedRoute of affectedOriginalRoutes.values()) {
            // Fetch the existing route to preserve its start_location
            // Without this, the re-sync would reset start_location to {lat:0, lng:0}
            // and skip route optimization
            let existingStartLocation: RouteStartLocation | undefined;
            try {
              const { data: existingRoute } = await getRouteByTechnicianDate(
                affectedRoute.technicianId,
                affectedRoute.expectedVisitDate
              );
              if (existingRoute?.route_data?.start_location) {
                existingStartLocation = existingRoute.route_data
                  .start_location as RouteStartLocation;
              }
            } catch {
              // If fetch fails, proceed without start location — route will still sync
            }

            await syncAssignmentsToRouteAsync(
              affectedRoute.technicianId,
              affectedRoute.expectedVisitDate,
              existingStartLocation
            );
          }
        }

        // Notify parent AFTER sync completes so refetch gets latest data
        onAssignmentChange?.();

        return results;
      } finally {
        setIsAssigning(false);
      }
    },
    [toast, onAssignmentChange]
  );

  /**
   * Sync assignments to the technician_routes table (background operation)
   * This fetches all assignments for the technician+date and creates/updates the route
   * @param startLocation - The starting location to use for the route (from UI selection)
   * @param knownAssignmentIds - Optional IDs that were just inserted/updated by the
   *   caller. The list-query (`getTechnicianAssignments`) below is subject to
   *   read-after-write visibility lag (replica/PgBouncer/etc), which historically
   *   silently dropped just-inserted rows and bailed without upserting the route —
   *   creating orphan assignments with route_id = NULL. When IDs are passed, we
   *   skip the 500ms wait and patch any race-induced misses by fetching them
   *   directly by primary key (PK lookups don't suffer the same lag).
   */
  const syncAssignmentsToRouteAsync = useCallback(
    async (
      technicianId: string,
      expectedVisitDate: string,
      startLocation?: RouteStartLocation,
      knownAssignmentIds?: string[]
    ) => {
      // eslint-disable-next-line no-console
      console.log('[syncAssignmentsToRouteAsync] Called with startLocation:', startLocation);

      try {
        // Race-condition workaround: only needed when we don't have known IDs.
        // With knownAssignmentIds, we patch any missed rows below via PK fetch.
        if (!knownAssignmentIds || knownAssignmentIds.length === 0) {
          // Small delay to ensure the database transaction has committed
          // This handles race conditions where we query before the insert is visible
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        // Fetch all assignments for this technician on this date
        // Note: getTechnicianAssignments returns an array directly, not { data: ... }
        const allAssignments = (await getTechnicianAssignments()) ?? [];
        // eslint-disable-next-line no-console
        console.log(
          '[syncAssignmentsToRouteAsync] Fetched all assignments:',
          allAssignments.length
        );

        // If the caller passed IDs of just-inserted/updated rows, verify the
        // re-query saw them; if any are missing, patch them in via direct PK
        // fetch. This eliminates the silent-orphan failure mode where the
        // list-query missed fresh inserts and the function bailed.
        if (knownAssignmentIds && knownAssignmentIds.length > 0) {
          const seenInRequery = new Set(allAssignments.map(a => a.id));
          const missingIds = knownAssignmentIds.filter(id => !seenInRequery.has(id));
          if (missingIds.length > 0) {
            // eslint-disable-next-line no-console
            console.log(
              `[syncAssignmentsToRouteAsync] Re-query missed ${missingIds.length} IDs; PK fetch`
            );
            try {
              const prodClient = getProdSupabaseClient();
              const { data: patchedRows, error: patchError } = await prodClient
                .from('technician_assignments')
                .select(
                  '*, technicians!inner(id, full_name, phone_number, email, skill_level, is_active)'
                )
                .in('id', missingIds);
              if (patchError) {
                // eslint-disable-next-line no-console
                console.warn('[syncAssignmentsToRouteAsync] PK-fetch patch failed:', patchError);
              } else if (patchedRows && Array.isArray(patchedRows)) {
                // Match the shape produced by getTechnicianAssignments
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const transformed = (patchedRows as any[]).map((a: any) => ({
                  ...a,
                  technician_name: a.technicians?.full_name,
                  technician_phone: a.technicians?.phone_number,
                  technician_email: a.technicians?.email,
                  technician_skill_level: a.technicians?.skill_level,
                  technician_active: a.technicians?.is_active
                }));
                allAssignments.push(...transformed);
              }
            } catch (patchCatchErr) {
              // eslint-disable-next-line no-console
              console.warn('[syncAssignmentsToRouteAsync] PK-fetch patch threw:', patchCatchErr);
            }
          }
        }

        if (allAssignments.length === 0) {
          // eslint-disable-next-line no-console
          console.log('[syncAssignmentsToRouteAsync] No assignments found, exiting');
          return;
        }

        // Filter to assignments for this technician on this date
        // Include ALL assignments for this tech/date, regardless of status. Completed
        // and cancelled work must stay in the route's JSONB so the Status Updates
        // section and Daily Route Summary can show the day's full picture. The prior
        // filter excluded them under the assumption they lived elsewhere — they don't,
        // and dropping them here orphans them from `route.recalls` on every re-sync,
        // which is exactly the data-corruption bug that wiped 6 of 7 Wade-Ford recalls
        // on 2026-05-31 (audit query found 67 affected routes across 10 tenants).
        const routeDate = formatRouteDate(expectedVisitDate);

        const technicianAssignments = allAssignments.filter(a => {
          if (a.technician_id !== technicianId) return false;
          if (!a.expected_visit_date) return false;
          const assignmentDate = formatRouteDate(a.expected_visit_date);
          return assignmentDate === routeDate;
        });

        console.log('[syncAssignmentsToRouteAsync] Filtered for technician/date:', {
          technicianId,
          routeDate,
          matchingAssignments: technicianAssignments.length
        });

        if (technicianAssignments.length === 0) {
          console.log(
            '[syncAssignmentsToRouteAsync] No assignments for this tech/date, deleting route'
          );
          await deleteRouteByTechnicianDate(technicianId, expectedVisitDate);
          return;
        }

        // Get the VINs from assignments to look up recall data
        const vins = technicianAssignments.map(a => a.vin);

        // Type for selected_recalls enrichment data
        interface SelectedRecallData {
          vin: string;
          campaign_number: string;
          car_year: number | null;
          make_name: string | null;
          model_name: string | null;
          dealer_address: string | null;
          dealer_city: string | null;
          dealer_region: string | null;
          dealer_postal_code: string | null;
          dealer_latitude: number | null;
          dealer_longitude: number | null;
          stock_number: string | null;
        }

        // Query selected_recalls to get vehicle and dealer info
        // This enriches the assignment data with info that's stored when recalls are selected
        const recallDataMap = new Map<string, SelectedRecallData>();

        try {
          const prodClient = getProdSupabaseClient();
          const { data: selectedRecalls } = await prodClient
            .from('selected_recalls')
            .select(
              'vin, campaign_number, car_year, make_name, model_name, dealer_address, dealer_city, dealer_region, dealer_postal_code, dealer_latitude, dealer_longitude, stock_number'
            )
            .in('vin', vins);

          if (selectedRecalls && Array.isArray(selectedRecalls)) {
            for (const recall of selectedRecalls as SelectedRecallData[]) {
              const key = `${recall.vin}|${recall.campaign_number}`;
              recallDataMap.set(key, recall);
            }
          }
        } catch {
          // If we can't fetch the enrichment data, continue with undefined values
        }

        // Transform assignments for route building with enriched data
        const assignmentsForRoute = technicianAssignments.map(a => {
          const recallKey = `${a.vin}|${a.campaign_number}`;
          const recallData = recallDataMap.get(recallKey);

          // Build base object with required fields
          const result: {
            id: string;
            vin: string;
            campaign_number: string;
            recall_title?: string;
            dealer_id?: string;
            dealer_name?: string;
            dealer_street?: string;
            dealer_city?: string;
            dealer_region?: string;
            dealer_postal_code?: string;
            dealer_phone?: string;
            dealer_latitude?: number;
            dealer_longitude?: number;
            vehicle_year?: number;
            vehicle_make?: string;
            vehicle_model?: string;
            estimated_duration_minutes?: number;
            assignment_status: string;
            stock_number?: string;
            // Manual stop fields
            stop_type?: 'recall' | 'manual';
            manual_stop_reason?: string;
            manual_stop_lat?: number;
            manual_stop_lng?: number;
            notes?: string;
            // Customer stops (set on the assignment by a DB trigger) — never names/phones
            stop_kind?: 'business' | 'customer';
            contact_id?: string | null;
            address_line2?: string | null;
            dispatcher_note?: string | null;
          } = {
            id: a.id,
            vin: a.vin,
            campaign_number: a.campaign_number,
            assignment_status: a.assignment_status
          };

          // Add optional fields only if they have values
          if (a.recall_title) result.recall_title = a.recall_title;
          if (a.dealer_id) result.dealer_id = a.dealer_id;
          if (a.dealer_name) result.dealer_name = a.dealer_name;
          if (a.estimated_duration_minutes)
            result.estimated_duration_minutes = a.estimated_duration_minutes;

          // Enrich with data from selected_recalls if available
          // Note: selected_recalls stores as dealer_address, but buildRouteDataFromAssignments expects dealer_street
          if (recallData?.dealer_address) result.dealer_street = recallData.dealer_address;
          if (recallData?.dealer_city) result.dealer_city = recallData.dealer_city;
          if (recallData?.dealer_region) result.dealer_region = recallData.dealer_region;
          if (recallData?.dealer_postal_code)
            result.dealer_postal_code = recallData.dealer_postal_code;
          if (recallData?.dealer_phone) result.dealer_phone = recallData.dealer_phone;
          if (recallData?.dealer_latitude) result.dealer_latitude = recallData.dealer_latitude;
          if (recallData?.dealer_longitude) result.dealer_longitude = recallData.dealer_longitude;
          if (recallData?.car_year) result.vehicle_year = recallData.car_year;
          if (recallData?.make_name) result.vehicle_make = recallData.make_name;
          if (recallData?.model_name) result.vehicle_model = recallData.model_name;
          if (recallData?.stock_number) result.stock_number = recallData.stock_number;

          // Fallback to assignment's vehicle info (for manual stops without selected_recalls)
          if (!result.vehicle_year && a.vehicle_year) result.vehicle_year = a.vehicle_year;
          if (!result.vehicle_make && a.vehicle_make) result.vehicle_make = a.vehicle_make;
          if (!result.vehicle_model && a.vehicle_model) result.vehicle_model = a.vehicle_model;

          // Fallback to assignment's dealer info (for manual stops without selected_recalls)
          if (!result.dealer_street && a.dealer_address) result.dealer_street = a.dealer_address;
          if (!result.dealer_city && a.dealer_city) result.dealer_city = a.dealer_city;
          if (!result.dealer_region && a.dealer_region) result.dealer_region = a.dealer_region;
          if (!result.dealer_postal_code && a.dealer_postal_code)
            result.dealer_postal_code = a.dealer_postal_code;

          // Include manual stop fields for route building
          if (a.stop_type) result.stop_type = a.stop_type;
          if (a.manual_stop_reason) result.manual_stop_reason = a.manual_stop_reason;
          if (a.manual_stop_lat) result.manual_stop_lat = a.manual_stop_lat;
          if (a.manual_stop_lng) result.manual_stop_lng = a.manual_stop_lng;
          if (a.notes) result.notes = a.notes;
          if (a.stop_kind) result.stop_kind = a.stop_kind;
          if (a.contact_id) result.contact_id = a.contact_id;
          if (a.address_line2) result.address_line2 = a.address_line2;
          if (a.dispatcher_note) result.dispatcher_note = a.dispatcher_note;

          return result;
        });

        // Sync to route table with the start location from UI selection
        // eslint-disable-next-line no-console
        console.log(
          '[syncAssignmentsToRouteAsync] Calling syncAssignmentsToRoute with startLocation:',
          startLocation
        );
        await syncAssignmentsToRoute(technicianId, routeDate, assignmentsForRoute, startLocation);

        // Invalidate all route queries so other pages get fresh data
        queryClient.invalidateQueries({ queryKey: routeKeys.all });
      } catch (error) {
        // Log error for debugging - route sync failures are critical
        // eslint-disable-next-line no-console
        console.error('[syncAssignmentsToRouteAsync] Route sync failed:', error);
      }
    },
    [queryClient]
  );

  /**
   * Undo a single assignment
   */
  const handleUndo = useCallback(
    async (assignmentId: string, technicianName: string) => {
      try {
        // Get assignment details before deleting so we can update the route
        const assignment = recentAssignments.find(a => a.id === assignmentId);
        const technicianId = assignment?.technician_id;
        const expectedVisitDate = assignment?.expected_visit_date;

        await deleteTechnicianAssignment(assignmentId);
        setRecentAssignments(prev => prev.filter(a => a.id !== assignmentId));

        logUserActivity({
          category: 'routing',
          action: 'undo_assignment',
          entityType: 'assignment',
          entityId: assignmentId,
          ...(assignment?.vin && { entityName: assignment.vin }),
          metadata: { technician_name: technicianName }
        });

        // Re-sync the route to reflect the deletion
        if (technicianId && expectedVisitDate) {
          syncAssignmentsToRouteAsync(technicianId, expectedVisitDate);
        }

        toast({
          title: 'Assignment Undone',
          description: `Removed assignment from ${technicianName}`
        });

        onAssignmentChange?.();
      } catch (error) {
        toast({
          title: 'Undo Failed',
          description: 'Could not undo the assignment',
          variant: 'destructive'
        });
      }
    },
    [toast, onAssignmentChange, recentAssignments, syncAssignmentsToRouteAsync]
  );

  /**
   * Undo multiple assignments
   */
  const handleBulkUndo = useCallback(
    async (assignmentIds: string[], technicianName: string) => {
      try {
        // Get unique technician + date combinations to re-sync routes after deletion
        const routesToSync = new Map<string, { technicianId: string; expectedVisitDate: string }>();
        for (const id of assignmentIds) {
          const assignment = recentAssignments.find(a => a.id === id);
          if (assignment?.technician_id && assignment?.expected_visit_date) {
            const key = `${assignment.technician_id}|${assignment.expected_visit_date}`;
            routesToSync.set(key, {
              technicianId: assignment.technician_id,
              expectedVisitDate: assignment.expected_visit_date
            });
          }
        }

        let undoneCount = 0;
        for (const id of assignmentIds) {
          try {
            await deleteTechnicianAssignment(id);
            undoneCount++;
          } catch {
            // Continue with other deletions
          }
        }

        setRecentAssignments(prev => prev.filter(a => !assignmentIds.includes(a.id)));

        if (undoneCount > 0) {
          logUserActivity({
            category: 'routing',
            action: 'bulk_undo_assignments',
            entityType: 'assignment',
            entityName: technicianName,
            itemCount: undoneCount,
            metadata: { technician_name: technicianName }
          });
        }

        // Re-sync all affected routes to reflect the deletions
        for (const { technicianId, expectedVisitDate } of routesToSync.values()) {
          syncAssignmentsToRouteAsync(technicianId, expectedVisitDate);
        }

        toast({
          title: 'Assignments Undone',
          description: `Removed ${undoneCount} assignment${undoneCount !== 1 ? 's' : ''} from ${technicianName}`
        });

        onAssignmentChange?.();
      } catch (error) {
        toast({
          title: 'Undo Failed',
          description: 'Could not undo some assignments',
          variant: 'destructive'
        });
      }
    },
    [toast, onAssignmentChange, recentAssignments, syncAssignmentsToRouteAsync]
  );

  /**
   * Clear recent assignments (e.g., when switching technicians)
   */
  const clearRecentAssignments = useCallback(() => {
    setRecentAssignments([]);
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
      undoTimeoutRef.current = null;
    }
  }, []);

  return {
    isAssigning,
    recentAssignments,
    assignRecall,
    bulkAssignRecalls,
    handleUndo,
    handleBulkUndo,
    clearRecentAssignments
  };
};

export default useAutoSaveAssignment;
